import { CardShell } from '@atlas/ui-components'
import { Chip, cn } from '@atlas/ui-primitives'
import type { Rule } from '@/domain/rules.types'
import { RuleSeverity } from '@/domain/rules.types'
import { RuleSeverityPill } from './RuleSeverityPill'

export interface RuleCardProps {
	rule: Rule
	/** Human-readable scope, derived from the rule's classification tuple. */
	scope: string
	/** Opens the rule detail (kept router-free: the page passes navigation). */
	onOpen?: () => void
}

/**
 * Rule result card (design §8.13): severity dot · statement · `id`/`type`/severity/
 * `scope` chips. Composes ui-primitives atoms + `RuleSeverityPill`. Presentational +
 * storied; promote to ui-domain when reused.
 */
export function RuleCard({ rule, scope, onOpen }: RuleCardProps) {
	const blocking = rule.severity === RuleSeverity.BLOCKING
	return (
		<CardShell as="article" className="p-4 shadow-ds-sm transition-shadow hover:shadow-ds-md">
			<div className="flex items-start gap-3">
				<span
					className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-ds-full', blocking ? 'bg-ds-danger' : 'bg-ds-warning')}
					title={rule.severity}
				/>
				<div className="flex-1">
					{onOpen ? (
						<button
							type="button"
							onClick={onOpen}
							className="block text-left text-sm leading-snug text-ds-text hover:underline"
						>
							{rule.statement}
						</button>
					) : (
						<p className="text-sm leading-snug text-ds-text">{rule.statement}</p>
					)}
					<div className="mt-2 flex flex-wrap items-center gap-1.5">
						<Chip variant="muted" size="sm" className="font-mono">
							{rule.id}
						</Chip>
						<Chip variant="muted" size="sm">
							{rule.type}
						</Chip>
						<RuleSeverityPill severity={rule.severity} />
						<Chip variant="muted" size="sm">
							{scope}
						</Chip>
					</div>
				</div>
			</div>
		</CardShell>
	)
}
